import { useTranslation } from 'react-i18next';
import Icon from '../ui/Icon';
import Reveal from '../ui/Reveal';
import PhotoSlot from './PhotoSlot';
import bambooLeft from '../../assets/careers/bamboo-left.webp';
import patternLeft from '../../assets/careers/pattern-left.webp';
import kasir from '../../assets/careers/kasir.webp';
import layanan from '../../assets/careers/layanan.webp';
import manajer from '../../assets/careers/manajer.webp';
import kebersamaan from '../../assets/careers/kebersamaan.webp';

// Four photographs dropped on a table: overlapping, each at its own slight
// angle. The rotations are deliberately uneven — a repeating tilt reads as a
// pattern rather than as photographs someone set down.
//
// Each photo is pre-cropped to its card's aspect ratio (src/assets/careers),
// so object-cover has nothing left to trim and the framing is the same at
// every viewport width.
const SHOTS = [
  { key: 'kasir', src: kasir, icon: 'user', side: 'left', className: 'left-0 top-0 z-20 w-[47%] rotate-[-3deg] aspect-[4/3.3]' },
  { key: 'layanan', src: layanan, icon: 'briefcase', side: 'right', className: 'right-0 top-[9%] z-10 w-[50%] rotate-[2.5deg] aspect-[4/2.9]' },
  { key: 'manajer', src: manajer, icon: 'user', side: 'left', className: 'left-[8%] bottom-0 z-30 w-[43%] rotate-[1.5deg] aspect-[4/3.2]' },
  { key: 'kebersamaan', src: kebersamaan, icon: 'heartHands', side: 'left', className: 'right-[1%] bottom-[4%] z-20 w-[41%] rotate-[-2.5deg] aspect-[4/2.9]' },
];

const TRUST = [
  { key: 'fees', icon: 'rupiah' },
  { key: 'official', icon: 'shieldCheck' },
  { key: 'simple', icon: 'checkCircle' },
];

export default function CareersHero() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden bg-[#fdf6ec]">
      {/* Left edge: batik behind, bamboo in front of it.
          The motif is a light tan on a cream ground — sampled, its mean is
          (251,239,221) against a (253,246,236) background, a difference of
          only a few units. It needs near-full opacity to register at all;
          anything subtle-sounding renders as literally nothing. */}
      <img
        src={patternLeft}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -left-6 top-0 z-0 h-full w-auto opacity-90"
      />

      {/* A narrow clipping strip rather than a wide image: the source is
          square, so sizing it to the hero's height would make it 745px wide
          and put stalks across the headline. The frame stays narrow and the
          image is scaled inside it. */}
      <div
        aria-hidden="true"
        style={{
          maskImage: 'linear-gradient(to bottom, #000 72%, transparent 98%)',
          WebkitMaskImage: 'linear-gradient(to bottom, #000 72%, transparent 98%)',
        }}
        className="pointer-events-none absolute inset-y-0 left-0 z-0 hidden w-28 overflow-hidden md:block lg:w-36 xl:w-44"
      >
        <img
          src={bambooLeft}
          alt=""
          className="absolute -left-8 top-0 h-full max-w-none origin-top animate-sway opacity-95"
        />
      </div>

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-10 px-4 pb-28 pt-12 sm:px-6 md:pb-36 md:pt-16 lg:grid-cols-[0.92fr_1.08fr] lg:gap-8 lg:px-8">
        {/* ---------- copy ---------- */}
        <div className="relative z-10 xl:pl-16">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
                {t('careers.eyebrow')}
              </span>
              <span className="h-px w-10 bg-primary-300" />
            </div>

            <h1 className="mt-4 font-serif text-5xl font-bold leading-[0.98] text-[#2b1a12] md:text-6xl lg:text-7xl">
              {t('careers.titleLead')}
              <br />
              <span className="text-primary-600">{t('careers.titleAccent')}</span>
            </h1>

            {/* Handwritten aside, sitting beside the headline as in the sketch */}
            <div className="mt-3 inline-block">
              <span className="block font-script text-2xl leading-tight text-[#7a5b3a] md:text-[28px]">
                {t('careers.scriptAccent')}
              </span>
              <svg viewBox="0 0 200 8" className="mt-0.5 h-2 w-44 text-primary-300" aria-hidden="true">
                <path d="M2 5c40-4 90-4 130-1s50 3 66 1" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>

            <p className="mt-5 max-w-md leading-relaxed text-gray-600">{t('careers.subtitle')}</p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#positions" className="btn-primary inline-flex items-center gap-2">
                <Icon name="briefcase" className="h-4 w-4" />
                {t('careers.seePositions')}
              </a>
              <a
                href="#process"
                className="inline-flex items-center gap-2 rounded-lg border border-primary-300 bg-white px-6 py-3 font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <Icon name="play" className="h-4 w-4" />
                {t('careers.howItWorks')}
              </a>
            </div>
          </Reveal>

          {/* Trust row. Fee scams are common enough here that this belongs
              above the fold, not in the FAQ. */}
          <Reveal delay={0.12} className="mt-9 grid gap-5 sm:grid-cols-3">
            {TRUST.map((item) => (
              <div key={item.key} className="flex items-start gap-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-primary-600 shadow-sm ring-1 ring-primary-100">
                  <Icon name={item.icon} className="h-4.5 w-4.5" />
                </span>
                <div>
                  <div className="text-sm font-bold text-gray-900">{t(`careers.trust.${item.key}.title`)}</div>
                  <p className="mt-0.5 text-xs leading-snug text-gray-500">
                    {t(`careers.trust.${item.key}.body`)}
                  </p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>

        {/* ---------- scattered photographs ---------- */}
        <Reveal delay={0.1} from="scale">
          <div className="relative mx-auto aspect-[4/3] w-full max-w-[560px]">
            {/* Positioning lives on a wrapper, not on PhotoSlot. PhotoSlot
                sets `relative` for its own label, and Tailwind emits
                `relative` after `absolute` in the sheet — so an `absolute`
                passed in through className silently loses and every card
                falls back into normal flow. */}
            {SHOTS.map((shot) => (
              <div
                key={shot.key}
                className={`absolute ${shot.className} transition-transform duration-300 hover:z-40 hover:!rotate-0`}
              >
                <PhotoSlot
                  className="h-full w-full"
                  src={shot.src}
                  alt={t(`careers.shots.${shot.key}`)}
                  loading="eager"
                  label={t(`careers.shots.${shot.key}`)}
                  labelIcon={shot.icon}
                  labelSide={shot.side}
                  hint={t('careers.photoHint')}
                />
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Red wave closing the hero */}
      <svg
        className="absolute inset-x-0 bottom-0 z-10 h-14 w-full md:h-24"
        viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true"
      >
        <path fill="#c0392b" d="M0 54c240 52 460 52 720 14s520-32 720 8v44H0z" />
      </svg>
    </section>
  );
}
